// =====================================================================================
// INVRT Radar — web console — the contacts table
//
// Name, Title, Company, Email, Phone, Source, Visits, When: the server's own Contact
// fields, rendered as received (a missing leaf is a dash, never a blank cell pretending to
// be a value). Each name is a button that opens the drawer, so the row works from the
// keyboard as well as the mouse; the checkbox column exists only when the caller can act on
// a selection (an organisation admin), because a selection with nothing to do is noise.
//
// The Visits column (C3) sorts on the client, on the facts the wire already carries: no new
// endpoint, no second request. A contact with no facts (a revealed person) never pretends
// to a position: it sorts after every counted contact in both directions.
// =====================================================================================

import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { ContactCard } from "@/components/ContactCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { formatDateTime } from "@/lib/format";
import type { Contact } from "@/lib/types.c1";
import { contactDisplayName, sourceLabel } from "./useContacts";

export interface ContactsTableProps {
  contacts: Contact[];
  /** Shows the checkbox column and wires the selection callbacks. */
  selectable: boolean;
  selected: ReadonlySet<string>;
  onToggle: (id: string) => void;
  /** Select or clear every row currently shown. */
  onToggleAll: (ids: string[], checked: boolean) => void;
  onOpen: (contact: Contact) => void;
  /**
   * When provided, an extra Journey column opens the COMPANY journey for the row's company
   * (V03) - a company-level surface, clearly separate from this row's own contact drawer.
   * Rows with neither a company domain nor a name disable the action: with no persisted
   * company evidence there is no deterministic journey to show.
   */
  onOpenJourney?: (contact: Contact) => void;
}

const DASH = "—";

/** The sort state of the Visits column: untouched, highest first, lowest first. */
type VisitsSort = "none" | "desc" | "asc";

const NEXT_VISITS_SORT: Record<VisitsSort, VisitsSort> = {
  none: "desc",
  desc: "asc",
  asc: "none",
};

const VISITS_SORT_LABEL: Record<VisitsSort, string> = {
  none: "Sort by visit count",
  desc: "Sorted by visit count, highest first. Activate for lowest first.",
  asc: "Sorted by visit count, lowest first. Activate to clear the sort.",
};

/** Rows ordered on the client from the facts the wire carries; ties and fact-less rows keep the server's order. */
function sortByVisits(contacts: Contact[], sort: Exclude<VisitsSort, "none">): Contact[] {
  const keyed = contacts.map((contact, index) => ({ contact, index }));
  keyed.sort((a, b) => {
    const aCount = a.contact.engagement?.visit_count;
    const bCount = b.contact.engagement?.visit_count;
    if (aCount === undefined && bCount === undefined) return a.index - b.index;
    if (aCount === undefined) return 1;
    if (bCount === undefined) return -1;
    if (aCount !== bCount) return sort === "desc" ? bCount - aCount : aCount - bCount;
    return a.index - b.index;
  });
  return keyed.map((entry) => entry.contact);
}

export function ContactsTable({
  contacts,
  selectable,
  selected,
  onToggle,
  onToggleAll,
  onOpen,
  onOpenJourney,
}: ContactsTableProps) {
  const [visitsSort, setVisitsSort] = useState<VisitsSort>("none");
  const rows = useMemo(
    () => (visitsSort === "none" ? contacts : sortByVisits(contacts, visitsSort)),
    [contacts, visitsSort],
  );
  const ids = contacts.map((contact) => contact.id);
  const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
  const someSelected = !allSelected && ids.some((id) => selected.has(id));

  return (
    <Table>
      <TableHead>
        <TableRow>
          {selectable ? (
            <TableHeaderCell className="w-10">
              <input
                type="checkbox"
                aria-label={allSelected ? "Clear the contacts on this page" : "Select every contact on this page"}
                checked={allSelected}
                ref={(element) => {
                  if (element) element.indeterminate = someSelected;
                }}
                onChange={(event) => onToggleAll(ids, event.target.checked)}
                className="h-4 w-4 rounded border-line text-primary focus:ring-primary"
              />
            </TableHeaderCell>
          ) : null}
          <TableHeaderCell>Name</TableHeaderCell>
          <TableHeaderCell>Title</TableHeaderCell>
          <TableHeaderCell>Company</TableHeaderCell>
          <TableHeaderCell>Email</TableHeaderCell>
          <TableHeaderCell>Phone</TableHeaderCell>
          <TableHeaderCell>Source</TableHeaderCell>
          <TableHeaderCell
            aria-sort={
              visitsSort === "desc" ? "descending" : visitsSort === "asc" ? "ascending" : undefined
            }
          >
            <button
              type="button"
              aria-label={VISITS_SORT_LABEL[visitsSort]}
              onClick={() => setVisitsSort(NEXT_VISITS_SORT[visitsSort])}
              className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              Visits
              {visitsSort === "none" ? (
                <ArrowUpDown className="h-3 w-3" aria-hidden="true" />
              ) : visitsSort === "desc" ? (
                <ArrowDown className="h-3 w-3" aria-hidden="true" />
              ) : (
                <ArrowUp className="h-3 w-3" aria-hidden="true" />
              )}
            </button>
          </TableHeaderCell>
          <TableHeaderCell>When</TableHeaderCell>
          {onOpenJourney ? <TableHeaderCell className="w-24">Journey</TableHeaderCell> : null}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((contact) => {
          const name = contactDisplayName(contact);
          return (
            <TableRow key={contact.id} className={selected.has(contact.id) ? "bg-primary-50/50" : undefined}>
              {selectable ? (
                <TableCell className="w-10">
                  <input
                    type="checkbox"
                    aria-label={`Select ${name}`}
                    checked={selected.has(contact.id)}
                    onChange={() => onToggle(contact.id)}
                    className="h-4 w-4 rounded border-line text-primary focus:ring-primary"
                  />
                </TableCell>
              ) : null}
              <TableCell className="font-medium">
                <button
                  type="button"
                  aria-label={`Open ${name}`}
                  onClick={() => onOpen(contact)}
                  className="text-left text-ink underline-offset-2 hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  {/* C5: the row card - the deterministic mark (aria-hidden) beside the
                      name, which stays in text and keeps the button's own accessible name. */}
                  <ContactCard variant="row" contact={contact} />
                </button>
                {contact.identified_level === "company" ? (
                  <span className="ml-2 text-xs text-ink-muted">Company only</span>
                ) : null}
              </TableCell>
              <TableCell className="text-ink-muted">{contact.person.title ?? DASH}</TableCell>
              <TableCell className="text-ink-muted">{contact.company.name ?? DASH}</TableCell>
              <TableCell className="font-mono text-xs">{contact.person.email ?? DASH}</TableCell>
              <TableCell className="font-mono text-xs">{contact.person.phone ?? DASH}</TableCell>
              <TableCell>
                <Badge tone={contact.source === "identified" ? "info" : "success"}>
                  {sourceLabel(contact.source)}
                </Badge>
              </TableCell>
              {/* C3: the visit facts exactly as the server counted them; a revealed person
                  carries none and shows the dash, never a zero pretending to be a fact. */}
              <TableCell className="whitespace-nowrap text-ink-muted">
                {contact.engagement ? contact.engagement.visit_count : DASH}
              </TableCell>
              <TableCell className="whitespace-nowrap text-ink-muted">
                {formatDateTime(contact.occurred_at)}
              </TableCell>
              {onOpenJourney ? (
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="min-h-11"
                    aria-label={`Company journey for ${contact.company.name ?? name}`}
                    disabled={contact.company.domain === null && contact.company.name === null}
                    onClick={() => onOpenJourney(contact)}
                  >
                    Journey
                  </Button>
                </TableCell>
              ) : null}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
